import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useQueryErrorToast } from './useQueryErrorToast';
import {
  Kitchen,
  ListProdControlMealsQuery,
  ListProdControlMealsQueryVariables,
  Session,
  SetProdControlMealMutation,
  SetProdControlMealMutationVariables,
  SubscribeToProdControlMealChangesSubscription,
  SubscribeToProdControlMealChangesSubscriptionVariables,
  UpdateProdControlMealMutation,
  UpdateProdControlMealMutationVariables
} from '../libs';
import {
  LIST_PROD_CONTROL_MEALS_QUERY,
  SET_PROD_CONTROL_MEAL_MUTATION,
  SUBSCRIBE_TO_PROD_CONTROL_MEAL_CHANGES_SUBSCRIPTION,
  UPDATE_PROD_CONTROL_MEAL_MUTATION
} from '../libs/graphQL';
import { prodControlMealConflictErrorType, getAppSyncErrorType } from '../libs/utils/getAppSyncErrorType';

type UseProdControlMealsProps = {
  kitchen: Kitchen;
  day: string;
  actor: string;
  shift: Session;
};

const conflictMessage = 'Quantity was changed by someone else — reloaded to the latest value.';

export const composeProdControlMealKey = (foodId: string, size: string, shift: Session): string => `${foodId}#${size}#${shift}`;

const bucketOf = (shift?: string | null): Session => (shift as Session) ?? Session.all;

const isInView = (event: { day: string; shift?: string | null }, day: string, shift: Session): boolean =>
  event.day === day && (shift === Session.all || bucketOf(event.shift) === shift);

const useProdControlMeals = ({ kitchen, day, actor, shift }: UseProdControlMealsProps) => {
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const { data, loading, error, refetch } = useQuery<ListProdControlMealsQuery, ListProdControlMealsQueryVariables>(
    LIST_PROD_CONTROL_MEALS_QUERY,
    {
      variables: { kitchen, day, shift },
      fetchPolicy: 'network-only'
    }
  );

  useQueryErrorToast(error);

  useEffect(() => {
    const meals = data?.listProdControlMeals ?? [];
    setQuantities(
      Object.fromEntries(
        meals.map((meal) => [composeProdControlMealKey(meal.foodId, meal.size, bucketOf(meal.shift)), meal.quantity])
      )
    );
  }, [data]);

  const [updateProdControlMeal] = useMutation<UpdateProdControlMealMutation, UpdateProdControlMealMutationVariables>(
    UPDATE_PROD_CONTROL_MEAL_MUTATION
  );
  const [setProdControlMeal] = useMutation<SetProdControlMealMutation, SetProdControlMealMutationVariables>(
    SET_PROD_CONTROL_MEAL_MUTATION
  );

  useSubscription<SubscribeToProdControlMealChangesSubscription, SubscribeToProdControlMealChangesSubscriptionVariables>(
    SUBSCRIBE_TO_PROD_CONTROL_MEAL_CHANGES_SUBSCRIPTION,
    {
      variables: { kitchen },
      onData: ({ data: subscriptionData }) => {
        const changed = subscriptionData.data?.subscribeToProdControlMealChanges;
        if (!changed || !isInView(changed, day, shift)) {
          return;
        }
        setQuantities((previous) => ({
          ...previous,
          [composeProdControlMealKey(changed.foodId, changed.size, bucketOf(changed.shift))]: changed.quantity
        }));
      }
    }
  );

  const handleMutationError = useCallback(
    async (error: unknown) => {
      if (getAppSyncErrorType(error) === prodControlMealConflictErrorType) {
        await refetch();
        toast.error(conflictMessage);
        return;
      }
      toast.error(error instanceof Error ? error.message : 'Internal error');
    },
    [refetch]
  );

  const adjust = useCallback(
    async (foodId: string, size: string, delta: number) => {
      try {
        const response = await updateProdControlMeal({
          variables: { kitchen, day, foodId, size, delta, actor, shift }
        });
        const updated = response.data?.updateProdControlMeal;
        if (updated) {
          setQuantities((previous) => ({ ...previous, [composeProdControlMealKey(foodId, size, shift)]: updated.quantity }));
        }
      } catch (error) {
        await handleMutationError(error);
      }
    },
    [updateProdControlMeal, kitchen, day, actor, shift, handleMutationError]
  );

  const setQuantity = useCallback(
    async (foodId: string, size: string, quantity: number, expectedQuantity: number) => {
      try {
        const response = await setProdControlMeal({
          variables: { kitchen, day, foodId, size, quantity, expectedQuantity, actor, shift }
        });
        const updated = response.data?.setProdControlMeal;
        if (updated) {
          setQuantities((previous) => ({ ...previous, [composeProdControlMealKey(foodId, size, shift)]: updated.quantity }));
        }
      } catch (error) {
        await handleMutationError(error);
      }
    },
    [setProdControlMeal, kitchen, day, actor, shift, handleMutationError]
  );

  return { quantities, loading, adjust, setQuantity };
};

export default useProdControlMeals;
